<template>
  <FormModal ref="createResponseModal" title="Create New Response" id="create_response_form_modal">
    <CreateResponse :template="template" ref="CreateResponseForm" :responses="responses" @close="closeCreateModal('createResponseModal')" @created="onResponseCreated" />
  </FormModal>

  <FormModal ref="editResponseModal" title="Edit Response" id="edit_response_form_modal">
    <EditResponse ref="EditResponseForm" @updated="onResponseUpdated" @close="closeEditModal" />
  </FormModal>
  
  <div class="response-list">
    <div class="responses-container">
      <!-- Local Responses Section -->
      <div class="response-section">
        <h3 class="section-title">Multiple Choice Responses</h3>
        <div class="responses-grid">
          <div class="text-end">
            <button class="btn btn-light py-2 btn-sm" style="font-size: 11px !important;" @click="openCreateModal('createResponseModal')">
              + Add Response
            </button>
          </div>
          <div 
            v-for="response in localResponses" 
            :key="response.id" 
            class="response-card"
          >
            <div class="response-header">
              <span class="response-name fw-bold"><i class="fa-regular fa-rectangle-list text-dark"></i> {{ response.name }}</span>
              <div class="response-actions">
                <button class="btn btn-sm btn-link p-0 me-1" @click.stop="editResponse(response)" title="Edit">
                  <i class="fas fa-pen text-primary"></i>
                </button>
                <button class="btn btn-sm btn-link p-0" @click.stop="deleteResponse(response)" title="Delete">
                  <i class="fas fa-trash text-danger"></i>
                </button>
              </div>
            </div>
            <div class="response-options" @click="selectResponse(response)">
              <span 
                v-for="option in response.get_options" 
                :key="option.id" 
                class="option-badge"
                :style="{ backgroundColor: option.bg || '#667eea' }"
              >
                {{ option.name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Generic Responses Section -->
      <div class="response-section">
        <h3 class="section-title">Generic Responses</h3>
        <div class="generic-grid">
          <div 
            v-for="response in genericResponses" 
            :key="response.id" 
            class="generic-item"
            @click="selectResponse(response)"
          >
            <i :class="response.icon || 'fa-regular fa-rectangle-list'" class="generic-icon"></i>
            <span class="generic-name">{{ response.name }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CreateResponse from '@/views/Template/CreateResponse.vue'
import EditResponse from '@/views/Template/Includes/EditResponse.vue'
import FormModal from '@/components/utils/FormModal.vue'
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'ResponseList',
  props: {
    template: {
      type: Object,
      required: true
    },
    responses: {
      type: Array,
      required: true
    }
  },
  components: {
    FormModal,
    CreateResponse,
    EditResponse,
  },
  emits: ['select', 'response-updated'],
  data() {
    return {
      editingResponse: null
    }
  },
  methods: {
    closeCreateModal(ref) {
      this.$refs[ref].closeModal()
    },
    openCreateModal(ref) {
      this.$refs[ref].showModal()
    },
    closeEditModal() {
      this.$refs.editResponseModal.closeModal()
      this.editingResponse = null
    },
    openEditModal() {
      this.$refs.editResponseModal.showModal()
    },
    selectResponse(response) {
      this.$emit('select', response)
    },
    editResponse(response) {
      this.editingResponse = response
      this.$nextTick(() => {
        this.openEditModal()
        setTimeout(() => {
          if (this.$refs.EditResponseForm && this.editingResponse) {
            this.$refs.EditResponseForm.setResponseData(this.editingResponse)
          }
        }, 200)
      })
    },
    onResponseCreated() {
      // Refresh responses from parent
      this.$emit('response-updated')
    },
    onResponseUpdated(updatedResponse) {
      // Update the response in the local list
      const index = this.responses.findIndex(r => r.id === updatedResponse.id)
      if (index !== -1) {
        // Use Vue.set or assign to trigger reactivity
        this.responses[index] = updatedResponse
      }
      // Emit to parent to refresh
      this.$emit('response-updated')
      this.closeEditModal()
    },
    async deleteResponse(response) {
      if (!confirm(`Are you sure you want to delete response "${response.name}"?`)) {
        return
      }

      try {
        const result = await form_api_submit({
          url: 'template/builder/response/delete/' + response.id,
          method: 'DELETE',
          show_success_toast: true,
          show_error_toast: true
        })

        if (result.success) {
          this.$toast('Response deleted successfully!', true)
          // Remove from local list
          const index = this.responses.findIndex(r => r.id === response.id)
          if (index !== -1) {
            this.responses.splice(index, 1)
          }
          this.$emit('response-updated')
        }
      } catch (error) {
        console.error('Error deleting response:', error)
        this.$toast('Failed to delete response', false)
      }
    }
  },
  computed: {
    localResponses() {
      return this.responses.filter(r => r.type === 'multiple_choice' || r.type === 'local')
    },
    genericResponses() {
      return this.responses.filter(r => r.type !== 'multiple_choice' && r.type !== 'local')
    }
  }
}
</script>

<style scoped>
.response-list {
  padding: 15px;
}

.responses-container {
  display: flex;
  gap: 20px;
}

.response-section {
  flex: 1;
}

.section-title {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e0e0e0;
  color: #666;
}

.responses-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.response-card {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 4px;
  cursor: pointer;
}

.response-card:hover {
  background: rgb(245, 245, 245);
}

.response-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.response-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.response-actions .btn-link {
  font-size: 12px;
}

.response-actions .btn-link:hover {
  background: #f0f0f0;
}

.response-icon {
  width: 16px;
  color: #667eea;
  font-size: 12px;
}

.response-name {
  font-weight: 500;
  font-size: 11px;
  color: #333;
}

.response-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.option-badge {
  padding: 1px 10px;
  border-radius: 8px;
  font-size: 9.5px;
  font-weight: 400;
  color: white;
}

/* Generic Responses */
.generic-grid {
  /* display: flex;
  flex-wrap: wrap;
  gap: 8px; */
  /* margin-top: 15px; */
}

.generic-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 510;
  padding: 5px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  margin-top: 4px;
}

.generic-item:hover {
  background: #e9ecef;
}

.generic-icon {
  font-size: 12px;
  color: #667eea;
}

.generic-name {
  font-size: 11px;
  color: #555;
}
</style>